import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { Download, Calendar, Loader2, Search, X } from 'lucide-react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { DashboardTabsContainer, PipelineFilters } from '@/components/dashboard/DashboardTabsContainer';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { LeadWithMetrics } from '@/types/dashboard';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const STAGES = ['NEW', 'CONTACTED', 'DISCUSSION', 'PROPOSAL', 'NEGOTIATION', 'WON', 'LOST'] as const;
const PRIORITIES = ['HOT', 'WARM', 'COLD'] as const;

const STAGE_COLORS: Record<string, string> = {
  NEW: 'bg-blue-100 text-blue-800 border-blue-200',
  CONTACTED: 'bg-cyan-100 text-cyan-800 border-cyan-200',
  DISCUSSION: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  PROPOSAL: 'bg-purple-100 text-purple-800 border-purple-200',
  NEGOTIATION: 'bg-orange-100 text-orange-800 border-orange-200',
  WON: 'bg-green-100 text-green-800 border-green-200',
  LOST: 'bg-red-100 text-red-800 border-red-200',
};

const PRIORITY_COLORS: Record<string, string> = {
  HOT: 'bg-red-100 text-red-800 border-red-200',
  WARM: 'bg-orange-100 text-orange-800 border-orange-200',
  COLD: 'bg-blue-100 text-blue-800 border-blue-200',
};

export const PipelineOverviewPage: React.FC = () => {
  const { toast } = useToast();
  const [isExporting, setIsExporting] = useState(false);

  // Filter state
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedStages, setSelectedStages] = useState<string[]>([]);
  const [selectedPriority, setSelectedPriority] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounce search input
  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(searchInput);
    }, 400);
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [searchInput]);

  // Build filters object for DashboardTabsContainer
  const filters: PipelineFilters = useMemo(() => {
    const f: PipelineFilters = {};
    if (debouncedSearch.trim()) f.search = debouncedSearch.trim();
    if (selectedStages.length > 0) f.stage = selectedStages.join(',');
    if (selectedPriority) f.priority = selectedPriority;
    if (dateFrom) f.dateFrom = dateFrom;
    if (dateTo) f.dateTo = dateTo;
    return f;
  }, [debouncedSearch, selectedStages, selectedPriority, dateFrom, dateTo]);

  const hasActiveFilters = debouncedSearch || selectedStages.length > 0 || selectedPriority || dateFrom || dateTo;

  const clearAllFilters = () => {
    setSearchInput('');
    setDebouncedSearch('');
    setSelectedStages([]);
    setSelectedPriority('');
    setDateFrom('');
    setDateTo('');
  };

  const toggleStage = (stage: string) => {
    setSelectedStages(prev =>
      prev.includes(stage) ? prev.filter(s => s !== stage) : [...prev, stage]
    );
  };

  const handleExportAll = useCallback(async () => {
    setIsExporting(true);
    try {
      const token = localStorage.getItem('authToken');

      // Build export URL with current filters
      const params = new URLSearchParams();
      if (filters.search) params.set('search', filters.search);
      if (filters.stage) params.set('stage', filters.stage);
      if (filters.priority) params.set('priority', filters.priority);
      if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.set('dateTo', filters.dateTo);
      params.set('limit', '500'); // Export up to 500 leads
      const qs = params.toString();

      const response = await fetch(`${API_BASE_URL}/lead-metrics/dashboard/total-pipeline${qs ? `?${qs}` : ''}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });
      if (response.status === 403) throw new Error('You don\'t have permission for this action');
      if (!response.ok) throw new Error('Failed to fetch pipeline data');
      const result = await response.json();
      const leads: LeadWithMetrics[] = result.success && result.data ? result.data.leads : result.leads || [];

      if (!leads || leads.length === 0) {
        toast({ title: 'No data to export', description: 'There are no leads matching the current filters to export.', variant: 'destructive' });
        return;
      }

      const escapeCSV = (val: string): string => {
        let safe = val;
        if (/^[=+\-@\t\r]/.test(safe)) {
          safe = "'" + safe;
        }
        if (safe.includes(',') || safe.includes('"') || safe.includes('\n')) {
          return `"${safe.replace(/"/g, '""')}"`;
        }
        return safe;
      };

      const headers = ['Lead Name', 'Stage', 'Deal Value', 'Priority', 'Assigned To', 'Last Activity', 'Days in Stage'];
      const rows = leads.map((lead) => [
        escapeCSV(lead.name || ''),
        lead.stage || '',
        lead.dealValue != null ? String(lead.dealValue) : '0',
        lead.priority || '',
        escapeCSV(lead.assignedToUser?.name || lead.assignedTo || 'Unassigned'),
        lead.lastActivity ? new Date(lead.lastActivity).toLocaleDateString('en-IN') : 'N/A',
        String(lead.daysInStage ?? 0)
      ]);

      const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `pipeline-export-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast({ title: 'Export complete', description: `Exported ${leads.length} leads to CSV.` });
    } catch (err) {
      toast({ title: 'Export failed', description: 'Could not export pipeline data. Please try again.', variant: 'destructive' });
    } finally {
      setIsExporting(false);
    }
  }, [toast, filters]);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Pipeline Overview</h1>
            <p className="text-gray-600 mt-1">Monitor your pipeline across different views</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportAll}
              disabled={isExporting}
              className="flex items-center gap-2"
            >
              {isExporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              {isExporting ? 'Exporting...' : 'Export All'}
            </Button>
          </div>
        </div>

        {/* Filter Bar */}
        <Card className="p-4">
          <div className="space-y-3">
            {/* Row 1: Search + Priority + Date Range */}
            <div className="flex flex-wrap items-center gap-3">
              {/* Search */}
              <div className="relative flex-1 min-w-[200px] max-w-[320px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search leads by name or company..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="w-full pl-9 pr-8 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                {searchInput && (
                  <button
                    onClick={() => { setSearchInput(''); setDebouncedSearch(''); }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>

              {/* Priority */}
              <div className="flex items-center gap-1.5">
                <span className="text-sm text-gray-500 font-medium">Priority:</span>
                {PRIORITIES.map((p) => (
                  <button
                    key={p}
                    onClick={() => setSelectedPriority(prev => prev === p ? '' : p)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                      selectedPriority === p
                        ? PRIORITY_COLORS[p]
                        : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>

              {/* Date Range */}
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-gray-400" />
                {/* Date presets */}
                {(['Today', 'This Week', 'This Month'] as const).map((preset) => {
                  const getPresetRange = () => {
                    const now = new Date();
                    const fmt = (d: Date) => d.toISOString().split('T')[0];
                    if (preset === 'Today') {
                      return { from: fmt(now), to: fmt(now) };
                    }
                    if (preset === 'This Week') {
                      const day = now.getDay();
                      const monday = new Date(now);
                      monday.setDate(now.getDate() - ((day + 6) % 7));
                      const sunday = new Date(monday);
                      sunday.setDate(monday.getDate() + 6);
                      return { from: fmt(monday), to: fmt(sunday) };
                    }
                    // This Month
                    const first = new Date(now.getFullYear(), now.getMonth(), 1);
                    const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
                    return { from: fmt(first), to: fmt(last) };
                  };
                  const range = getPresetRange();
                  const isActive = dateFrom === range.from && dateTo === range.to;
                  return (
                    <button
                      key={preset}
                      onClick={() => {
                        if (isActive) {
                          setDateFrom('');
                          setDateTo('');
                        } else {
                          setDateFrom(range.from);
                          setDateTo(range.to);
                        }
                      }}
                      className={`px-2.5 py-1.5 text-xs font-medium rounded-md border transition-colors ${
                        isActive
                          ? 'bg-blue-100 text-blue-700 border-blue-300'
                          : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      {preset}
                    </button>
                  );
                })}
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  title="From date"
                />
                <span className="text-gray-400 text-sm">to</span>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  title="To date"
                />
              </div>

              {/* Clear All */}
              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearAllFilters}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="h-3.5 w-3.5 mr-1" />
                  Clear filters
                </Button>
              )}
            </div>

            {/* Row 2: Stage filter chips */}
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-sm text-gray-500 font-medium mr-1">Stage:</span>
              {STAGES.map((stage) => (
                <button
                  key={stage}
                  onClick={() => toggleStage(stage)}
                  className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
                    selectedStages.includes(stage)
                      ? STAGE_COLORS[stage]
                      : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  {stage}
                </button>
              ))}
              {selectedStages.length > 0 && (
                <button
                  onClick={() => setSelectedStages([])}
                  className="ml-1 text-xs text-gray-400 hover:text-gray-600 underline"
                >
                  clear
                </button>
              )}
            </div>
          </div>
        </Card>

        {/* Dashboard Tabs Container - Main Content */}
        <DashboardTabsContainer filters={filters} />
      </div>
    </DashboardLayout>
  );
};
